import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { safeLocalStorage } from '@/lib/safe-storage';

import type { AdjustMode } from '@/lib/api/queries';

/**
 * =============================================================================
 *  chartPrefsStore —— 「設定指標」抽屜與範圍藥丸寫進來的地方。
 *
 *  §10.5.2 末段：「使用者的選擇寫入 `chartPrefsStore` 並同步到
 *  `app.user_prefs.chart_prefs`。」所以這個 store 的形狀就是那一個 jsonb 欄的形狀。
 *
 *  **這裡存的是「要畫哪些」，不是「畫出來的數字」。**
 *  指標的值永遠來自 `/ohlcv?indicators=`（§10.4.1 規則 5、公理二）。
 *  這個 store 裡出現任何一個數值陣列，就是瀏覽器開始自己算指標的第一步。
 * ========================================================================== */

export const RANGES = ['1M', '3M', '6M', '1Y', '3Y', '5Y', 'ALL'] as const;
export type RangeKey = (typeof RANGES)[number];

/** UI 的 `1Y` → API 的 `1y`。兩套字面值刻意不同名，才不會有人把它們當成同一個東西。 */
export const RANGE_TO_API: Record<RangeKey, string> = {
  '1M': '1m',
  '3M': '3m',
  '6M': '6m',
  '1Y': '1y',
  '3Y': '3y',
  '5Y': '5y',
  ALL: 'max',
};

export type Interval = '1d' | '1wk' | '1mo';

export interface ChartPrefsState {
  range: RangeKey;
  interval: Interval;
  /** 還原權值模式。**必填無預設的顯式參數**（§10.4.1）——儀表板固定落在 `raw`，
   * 因為 KPI 卡顯示的是「使用者當時在盤上看到的數字」。 */
  mode: AdjustMode;
  /** `/meta/indicators` 的 key，例如 `ma20`。順序即圖上的疊放順序。 */
  indicators: string[];
  /**
   * registry 裡 `chart_overlay=true` 的 metric_key（外資買賣超…）。
   *
   * **最多一個。** 型別留成陣列是因為 `app.user_prefs.chart_prefs.overlays` 已經是陣列、
   * 而且圖表遲早要能疊兩個 pane；但**今天** `PriceChart` 只畫一個籌碼 pane
   * （ARCHITECTURE §10.5.2：「籌碼疊圖 pane（可選）：外資買賣超 histogram」，單數）。
   *
   * 整合時這裡是「不受限的多選」，而畫面只畫得出第一個 —— 使用者把「投信買賣超」
   * 也打開，勾勾亮了、偏好也存了，圖上什麼都沒發生，也沒有任何說明。
   * 所以 {@link ChartPrefsState.toggleOverlay} 是**單選**：打開一個就關掉其他的，
   * 使用者立刻在抽屜裡看到那件事發生，而不是什麼都沒發生。
   */
  overlays: string[];
  showVolume: boolean;
  setRange: (range: RangeKey) => void;
  setInterval: (interval: Interval) => void;
  setMode: (mode: AdjustMode) => void;
  toggleIndicator: (key: string, on?: boolean) => void;
  /** **單選。** 打開一個籌碼疊圖會關掉原本那個；再點一次同一個則關閉。 */
  toggleOverlay: (metricKey: string, on?: boolean) => void;
  setShowVolume: (on: boolean) => void;
  /** `/me/prefs.chart_prefs` 回來時整批灌入。未知欄位一律忽略而不是崩潰。 */
  hydrateFromServer: (prefs: Record<string, unknown>) => void;
  /** 送去 `/me/prefs` 的形狀。 */
  toServer: () => Record<string, unknown>;
}

const STORAGE_KEY = 'stock.chartPrefs';

/** 開箱預設：截圖上那三條均線 + 成交量 + 外資買賣超 pane。 */
const DEFAULTS = {
  range: '1Y' as RangeKey,
  interval: '1d' as Interval,
  mode: 'raw' as AdjustMode,
  indicators: ['ma5', 'ma20', 'ma60'],
  overlays: ['inst_foreign_net'],
  showVolume: true,
};

function toggle(list: string[], key: string, on: boolean | undefined): string[] {
  const has = list.includes(key);
  const want = on ?? !has;
  if (want === has) return list;
  return want ? [...list, key] : list.filter((k) => k !== key);
}

function asStringArray(value: unknown): string[] | undefined {
  return Array.isArray(value) && value.every((v) => typeof v === 'string')
    ? (value as string[])
    : undefined;
}

export const useChartPrefsStore = create<ChartPrefsState>()(
  persist(
    (set, get) => ({
      ...DEFAULTS,
      setRange: (range) => {
        set({ range });
      },
      setInterval: (interval) => {
        set({ interval });
      },
      setMode: (mode) => {
        set({ mode });
      },
      toggleIndicator: (key, on) => {
        set((s) => ({ indicators: toggle(s.indicators, key, on) }));
      },
      toggleOverlay: (metricKey, on) => {
        set((s) => {
          const want = on ?? !s.overlays.includes(metricKey);
          // 單選：圖上只有一個籌碼 pane，所以 state 裡也只能有一個。
          // 用 toggle() 累積成多選會讓「勾了但沒反應」重新出現。
          return { overlays: want ? [metricKey] : [] };
        });
      },
      setShowVolume: (showVolume) => {
        set({ showVolume });
      },
      hydrateFromServer: (prefs) => {
        const patch: Partial<ChartPrefsState> = {};
        const range = prefs['range'];
        if (typeof range === 'string' && (RANGES as readonly string[]).includes(range)) {
          patch.range = range as RangeKey;
        }
        const interval = prefs['interval'];
        if (interval === '1d' || interval === '1wk' || interval === '1mo') {
          patch.interval = interval;
        }
        const mode = prefs['mode'];
        if (mode === 'raw' || mode === 'px' || mode === 'tr') patch.mode = mode;
        const indicators = asStringArray(prefs['indicators']);
        if (indicators !== undefined) patch.indicators = indicators;
        const overlays = asStringArray(prefs['overlays']);
        // 伺服器上可能留著整合期寫進去的多選值（app.user_prefs 是持久的），
        // 所以灌入時也要夾到 1 個 —— 否則舊帳號一登入就回到「勾了兩個、只畫一個」。
        if (overlays !== undefined) patch.overlays = overlays.slice(0, 1);
        if (typeof prefs['showVolume'] === 'boolean') patch.showVolume = prefs['showVolume'];
        set(patch);
      },
      toServer: () => {
        const s = get();
        return {
          range: s.range,
          interval: s.interval,
          mode: s.mode,
          indicators: s.indicators,
          overlays: s.overlays,
          showVolume: s.showVolume,
        };
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(safeLocalStorage),
      partialize: (state) => ({
        range: state.range,
        interval: state.interval,
        mode: state.mode,
        indicators: state.indicators,
        overlays: state.overlays,
        showVolume: state.showVolume,
      }),
      version: 1,
    },
  ),
);

export const CHART_PREFS_DEFAULTS = DEFAULTS;
